import { pdfjs } from 'react-pdf';

/**
 * pdf.js rejects a document it cannot parse with InvalidPDFException and wraps
 * any other parsing failure raised inside the worker as UnknownErrorException.
 */
const PDF_FORMAT_ERRORS = ['InvalidPDFException', 'UnknownErrorException'];
const PDF_HEADER_BYTES = 1024;
const PDF_PARSE_TIMEOUT_MS = 10000;

/*
 * Importing react-pdf points workerSrc at a pdf.worker.js the app does not
 * serve, so it is always replaced with the CDN worker the other pdf.js viewers
 * of the app use. Without it pdf.js cannot start and no PDF would be checked.
 */
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const readAsText = (blob) =>
  blob.text
    ? blob.text()
    : new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsText(blob);
      });

const isPdfFormatError = (error) => Boolean(error) && PDF_FORMAT_ERRORS.includes(error.name);

/**
 * Whether the first bytes of the file carry the %PDF- signature.
 *
 * @param {Blob} blob
 * @returns {Promise<boolean>}
 */
export const hasPdfSignature = async (blob) =>
  (await readAsText(blob.slice(0, PDF_HEADER_BYTES))).includes('%PDF-');

/**
 * Whether pdf.js can open the document behind the URL and its first page.
 *
 * A file with a valid %PDF- header but a damaged body passes the header check
 * and renders as a blank native viewer, so the structure has to be parsed to
 * detect it. pdf.js streams the object URL to its worker, so the file is not
 * copied once more in memory. Only PDF format errors count as unreadable: a
 * worker that cannot load, a parse slower than the timeout or an aborted
 * request keep the file previewable, so a network or CDN problem never reports
 * a valid file as corrupt nor leaves the preview loading forever.
 *
 * @param {string} url Object URL of the downloaded file.
 * @param {AbortSignal} [signal] Cancels the check when the preview is closed.
 * @returns {Promise<boolean>}
 */
export const isReadablePdf = (url, signal) => {
  if (signal && signal.aborted) return Promise.resolve(true);

  let loadingTask;
  try {
    loadingTask = pdfjs.getDocument({ url, disableRange: true });
  } catch {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    let timer = null;
    let settled = false;

    const settle = (readable) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (signal) signal.removeEventListener('abort', handleAbort);
      loadingTask.destroy();
      resolve(readable);
    };
    const handleAbort = () => settle(true);

    timer = setTimeout(() => settle(true), PDF_PARSE_TIMEOUT_MS);
    if (signal) signal.addEventListener('abort', handleAbort);

    loadingTask.promise
      .then((pdf) => pdf.getPage(1))
      .then(
        () => settle(true),
        (error) => settle(!isPdfFormatError(error))
      );
  });
};
